import { useState } from "react";
import { Check, ChevronsUpDown, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface TaxCategoryComboboxProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  onManage?: () => void;
}

export const TaxCategoryCombobox = ({
  value,
  options,
  onChange,
  onManage,
}: TaxCategoryComboboxProps) => {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => o.value === value)?.label || "";

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="h-auto min-h-9 w-full justify-between border-0 shadow-none focus:ring-0 bg-transparent text-left whitespace-normal px-2 overflow-hidden"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              {selectedLabel || <span className="text-muted-foreground">Select…</span>}
            </span>
            <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0 bg-popover"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search categories…" />
            <CommandList>
              <CommandEmpty>No category found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={(currentValue) => {
                      onChange(currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-1 h-4 w-4 shrink-0",
                        value === option.value
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <span className="text-xs">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {onManage && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 opacity-50 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onManage();
          }}
        >
          <Settings className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};
